<template>
  <div class="mb-4">
    <div
      v-if="showUnsavedWarning"
      class="alert alert-warning"
    >
      Some of the settings were auto-generated. Please save the form for settings to apply.
    </div>

    <h2>Expert assessment table</h2>
    <div class="card">
      <div class="card-header btn-wrapper">
        <button
          type="button"
          class="btn btn-sm btn-success"
          @click="showEditColumnsModal"
        >
          Change columns
        </button>
        <button
          type="button"
          class="btn btn-sm btn-success"
          @click="showEditQuestionsModal"
        >
          Change questions
        </button>
      </div>
      <table class="table expert-assesment-settings-table">
        <thead>
          <tr>
            <th class="tight">#</th>
            <th class="name">Name</th>
            <th
              v-for="column of reportSettings.columns"
              :key="column.id"
            >
              {{ column.title }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="question of enabledQuestions"
            :key="`question-${question.id}`"
          >
            <td class="tight">{{ question.nr }}</td>
            <td class="name">
              <input
                v-model="question.title"
                type="text"
                class="form-control form-control-sm"
                @change="updateSettings"
              >
            </td>
            <td
              v-for="column of reportSettings.columns"
              :key="column.id"
            >
              <select
                v-model="question.columns[column.id]"
                class="form-select form-select-sm"
                @change="updateSettings"
              >
                <option />
                <option
                  v-for="choice of question.choices"
                  :key="choice.id"
                  :value="choice.id"
                >
                  {{ choice.name }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <h2 class="mt-4">Overall training assessment question</h2>
    <div>
      <select
        v-model="reportSettings.overallAssessmentQuestionId"
        class="form-select form-select-sm"
        @change="updateSettings"
      >
        <option />
        <option
          v-for="question of assessmentQuestions"
          :key="question.id"
          :value="question.id"
        >
          {{ question.nr }}. {{ question.name }}
        </option>
      </select>
    </div>
    <div
      v-if="showPreview"
      class="mb-4 mt-4"
    >
      <button
        type="button"
        class="btn btn-sm btn-info"
        @click="previewReport"
      >
        Preview report (docx)
      </button>
    </div>

    <h2 class="mt-4">Additional survey settings</h2>
    <div class="mb-3">
      <label
        class="form-label"
        for="additional_survey_a"
      >
        1.A) JOB PERFORMANCE question (Single choice question)
      </label>
      <select
        id="additional_survey_a"
        v-model="reportSettings.additionalSurveyAQuestionId"
        class="form-select form-select-sm"
        @change="updateSettings"
      >
        <option />
        <option
          v-for="question of singleChoiceQuestions"
          :key="question.id"
          :value="question.id"
        >
          {{ question.nr }}. {{ question.name }}
        </option>
      </select>
    </div>
    <div class="mb-3">
      <label
        class="form-label"
        for="additional_survey_b"
      >
        1.B) OVERALL PERCEIVED IMPROVEMENT - TRAINING OBJECTIVES question (Ratings table question)
      </label>
      <select
        id="additional_survey_b"
        v-model="reportSettings.additionalSurveyBQuestionId"
        class="form-select form-select-sm"
        @change="updateSettings"
      >
        <option />
        <option
          v-for="question of ratingsTableQuestions"
          :key="question.id"
          :value="question.id"
        >
          {{ question.nr }}. {{ question.name }}
        </option>
      </select>
    </div>
    <div class="mb-3">
      <label
        class="form-label"
        for="additional_survey_c"
      >
        1.C) TRAINING OBJECTIVES question (Multi choice question)
      </label>
      <select
        id="additional_survey_c"
        v-model="reportSettings.additionalSurveyCQuestionId"
        class="form-select form-select-sm"
        @change="updateSettings"
      >
        <option />
        <option
          v-for="question of multiChoiceQuestions"
          :key="question.id"
          :value="question.id"
        >
          {{ question.nr }}. {{ question.name }}
        </option>
      </select>
    </div>

    <teleport
      to="#vue-portal-target"
      append
    >
      <survey-report-settings-columns-modal
        ref="editColumnsModal"
        @save="onChangeColumns"
      />
      <survey-report-settings-questions-modal
        ref="editQuestionsModal"
        @save="onChangeQuestions"
      />
    </teleport>
  </div>
</template>
<script setup lang="ts">
import {computed, onMounted, ref, useTemplateRef} from "vue"
import type {PropType} from "vue"
import Rails from "@rails/ujs"
import axios from "axios"
import SurveyReportSettingsColumnsModal from "./survey-report-settings-columns-modal.vue"
import SurveyReportSettingsQuestionsModal from "./survey-report-settings-questions-modal.vue"
import type {
  SurveyReportColumn,
  SurveyReportData,
  SurveyReportExpertAssessmentQuestion,
  SurveyReportQuestion,
  SurveyReportSettings,
} from "./survey-types"
import {QuestionType} from "./survey-types"
import {isPresent} from "../js-utils"

const props = defineProps({
  previewUrl: {default: null, type: String},
  surveyData: {default: null, type: Object as PropType<SurveyReportData | null>},
})

const editColumnsModal = useTemplateRef<InstanceType<typeof SurveyReportSettingsColumnsModal>>("editColumnsModal")
const editQuestionsModal = useTemplateRef<InstanceType<typeof SurveyReportSettingsQuestionsModal>>("editQuestionsModal")

const settings = ref<SurveyReportData>({
  saved: false,
  surveyQuestions: [],
})

const reportSettings = ref<SurveyReportSettings>({
  columns: [],
  questions: [],
  overallAssessmentQuestionId: null,
  additionalSurveyAQuestionId: null,
  additionalSurveyBQuestionId: null,
  additionalSurveyCQuestionId: null,
})

const settingsField = ref<HTMLInputElement | null>(null)

const showUnsavedWarning = computed((): boolean => settings.value.saved != true)

const showPreview = computed((): boolean => isPresent(props.previewUrl))

const enabledQuestions = computed((): SurveyReportQuestion[] => {
  const result = [] as SurveyReportQuestion[]
  for (const q of reportSettings.value.questions) {
    if (q.enabled) {
      result.push(q)
    }
  }
  return result
})

const assessmentQuestions = computed((): SurveyReportExpertAssessmentQuestion[] => (
  settings.value.surveyQuestions.filter(q => [QuestionType.singleChoice, QuestionType.rating].includes(q.typeId))
))

const singleChoiceQuestions = computed((): SurveyReportExpertAssessmentQuestion[] => (
  settings.value.surveyQuestions.filter(q => q.typeId == QuestionType.singleChoice)
))

const multiChoiceQuestions = computed((): SurveyReportExpertAssessmentQuestion[] => (
  settings.value.surveyQuestions.filter(q => q.typeId == QuestionType.multipleChoice)
))

const ratingsTableQuestions = computed((): SurveyReportExpertAssessmentQuestion[] => (
  settings.value.surveyQuestions.filter(q => q.typeId == QuestionType.ratingsTable)
))

const updateSettings = (): void => {
  if (settingsField.value) {
    settingsField.value.value = JSON.stringify(reportSettings.value)
  }
}

const previewReport = (): void => {
  if (settingsField.value == null || props.previewUrl == null) {
    return
  }

  axios.post(props.previewUrl, {
    report_settings: settingsField.value.value,
  }, {
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": Rails.csrfToken(),
    },
  })
    .then((response) => {
      if (response.data) {
        if (response.data.url) {
          window.location.href = response.data.url
        }
      }
    })
}

const showEditColumnsModal = (): void => {
  const columns = JSON.parse(JSON.stringify(reportSettings.value.columns)) as SurveyReportColumn[]
  editColumnsModal.value?.showModal(columns)
}

const onChangeColumns = (newColumns: SurveyReportColumn[]): void => {
  reportSettings.value.columns = JSON.parse(JSON.stringify(newColumns)) as SurveyReportColumn[]
  updateSettings()
  editColumnsModal.value?.hideModal()
}

const showEditQuestionsModal = (): void => {
  const questions = JSON.parse(JSON.stringify(reportSettings.value.questions)) as SurveyReportQuestion[]
  editQuestionsModal.value?.showModal(questions)
}

const onChangeQuestions = (newQuestions: SurveyReportQuestion[]): void => {
  reportSettings.value.questions = JSON.parse(JSON.stringify(newQuestions)) as SurveyReportQuestion[]
  updateSettings()
  editQuestionsModal.value?.hideModal()
}

onMounted(() => {
  const fieldEl = document.querySelector('input[ref="report-settings"]')
  if (fieldEl instanceof HTMLInputElement) {
    settingsField.value = fieldEl
    reportSettings.value = JSON.parse(settingsField.value.value) as SurveyReportSettings
  }
  if (props.surveyData) {
    settings.value = props.surveyData
  }
})
</script>
